<template>
  <div class="layer">
    <div class="inner">
      <button type="button" class="btnClose" @click="close"></button>
      <h2 class="layerTitle">{{locale.option_settings}}</h2>
      <div v-if="isReady" class="innerContent">
        <div class="info">
          <div class="mb30">
            <div class="innerTitle setLabel">{{locale.title}}</div>
            <input type="text" class="input" :disabled="isReadonly" v-model="title">
          </div>
          <div class="mb30 isRequired">
            <div class="innerTitle setLabel">{{locale.name}}</div>
            <SetLanguageValue
              :locale="locale"
              :langValue="name"
              :disabled="isReadonly" />
          </div>
          <div class="mb30">
            <div class="innerTitle setLabel">{{locale.required_option}}</div>
            <span class="checkbox">
              <input type="checkbox" id="option_required" :disabled="isReadonly" v-model="isRequired">
              <label for="option_required">{{locale.required}}</label>
            </span>
          </div>
          <div class="mb30 isRequired">
            <div class="innerTitle setLabel">{{locale.option_type}}</div>
            <div class="mb5">
              <div class="flexContainer mb5">
                <div class="flexItem">
                  <span class="checkbox">
                    <input type="radio" name="option_type" id="option_type_check" value="check" :disabled="isReadonly" v-model="type">
                    <label for="option_type_check">{{locale.checkbox}}</label>
                  </span>
                </div>
                <div class="flexItem">
                  <span class="checkbox">
                    <input type="radio" name="option_type" id="option_type_card" value="card" :disabled="isReadonly" v-model="type">
                    <label for="option_type_card">{{locale.card}}</label>
                  </span>
                </div>
              </div>
              <div class="flexContainer">
                <div class="flexItem">
                  <span class="checkbox">
                    <input type="radio" name="option_type" id="option_type_select" value="select" :disabled="isReadonly" v-model="type">
                    <label for="option_type_select">{{locale.dropdown}}</label>
                  </span>
                </div>
                <div class="flexItem">
                  <span class="checkbox">
                    <input type="radio" name="option_type" id="option_type_input" value="input" :disabled="isReadonly" v-model="type">
                    <label for="option_type_input">{{locale.direct_input}}</label>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="isRequired">
            <div class="innerTitle setLabel">{{locale.option_selection_criteria}}</div>
            <div class="mb5">
              {{locale.min}} <input type="text" class="input sizeS" @keyup="setValue('min', min)" :disabled="isReadonly || type === 'select' || type === 'input'" v-model="min"> {{locale.items}}
            </div>
            <div>
              {{locale.max}} <input type="text" class="input sizeS" @keyup="setValue('max', max)" :disabled="isReadonly || type === 'select' || type === 'input'" v-model="max"> {{locale.items}}
            </div>
            <p v-if="!isValid.isValidMin" class="colorRed">{{locale.validate_empty_min}}</p>
            <p v-else-if="!isValid.isValidMax" class="colorRed">{{locale.validate_empty_max}}</p>
            <p v-else-if="!isValid.isValidMinMax" class="colorRed">{{locale.validate_min_gt_max}}</p>
          </div>
        </div>
        <div class="info setOptionItems isRequired">
          <div class="innerTitle setLabel">{{locale.option_item}}</div>
          <div v-if="type !== 'input' && !isBaseShopPos && !isReadonly" class="mb10">
            <button class="btn btnWhite" @click="addOptionItem">(+) {{locale.option_item}}</button>
            <button class="btn btnWhite" v-if="!mustPosSync" @click="setItemListAll(true)">{{locale.add_product_as_option}}</button>
          </div>
          <div class="listTable">
            <table class="optionItemList">
              <thead>
                <tr>
                  <th v-if="isSavedOption" class="center">{{locale.activate}}</th>
                  <th></th>
                  <th>{{locale.name}}</th>
                  <th>
                    {{locale.price}}
                    <p class="theadTip">{{locale.show_plus_before_price}}</p>
                  </th>
                  <th>{{locale.product_id}}</th>
                  <th v-if="type === 'card'" class="center">{{locale.image}}</th>
                  <th>{{locale.max_quantity}}</th>
                  <th v-if="!(isBaseShopPos && mustPosSync)">{{ locale.sub_option }}</th>
                  <th v-if="!isApply || !isBaseShopPos" class="center">{{locale.delete}}</th>
                </tr>
              </thead>
              <draggable v-if="type !== 'input' && optionItems.length > 0"
                v-model="optionItems"
                tag="tbody"
                :animation="200"
                :disabled="isReadonly">
                <tr
                  v-for="(item, index) in optionItems"
                  :key="index"
                  :class="{inActivateItem: isApply ? !item.isActive : false}">
                  <td v-if="isApply && isSavedOption" class="center">
                    <button
                      type="button"
                      class="btn btnLightGrey sizeXs"
                      :class="{btnBlue: item.isActive}"
                      @click="activateItem(index)">
                      {{ locale.activate }}
                    </button>
                  </td>
                  <td class="itemCheck">
                    <span class="checkbox">
                      <input
                        type="checkbox"
                        :id="`option_default_check_${index}`"
                        :checked="item.isDefault"
                        :disabled="isReadonly || item.subOption?.length > 0"
                        @click="setDefaultItem($event, index)">
                      <label :for="`option_default_check_${index}`"></label>
                    </span>
                  </td>
                  <td class="optionItemName">
                    <SetLanguageValue
                      :locale="locale"
                      :langValue="item.name"
                      :disabled="isReadonly" />
                    <!-- 옵션선택 페이지에서의 옵션아이템 이름과 리스트에서 노출되는 옵션아이템 이름을 분리하여 관리 -->
                    <button type="button" class="btnSetting" @click.stop="setAdvancedSetting(item)"></button>
                    <!-- //옵션선택 페이지에서의 옵션아이템 이름과 리스트에서 노출되는 옵션아이템 이름을 분리하여 관리 -->
                    <div v-if="item.subOption" class="subOptionItems">
                      <div v-for="sub in item.subOption"
                        :key="sub.id"
                        class="subOption"
                        :class="{isDeleted: !isValidSubOption(sub.id)}">
                        <span v-if="!isValidSubOption(sub.id)">(deleted)</span>
                        {{ getSubOptionName(sub.id) }}
                        <button
                          type="button"
                          class="btnDel"
                          @click.stop="delSubOption(item.optionItemId, sub.id)">
                        </button>
                      </div>
                    </div>
                  </td>
                  <td class="optionItemPrice">
                    <button
                      type="button"
                      class="additionalCharge"
                      :class="{isActive: item.showPlus}"
                      @click="item.showPlus = !item.showPlus">
                      +
                    </button>
                    <input
                      type="text"
                      :disabled="isReadonly || item.shopItemId"
                      v-model="item.price"
                      @keyup="setOptionPrice(item)"
                      class="input sizeSm">
                  </td>
                  <td>
                    <p v-if="item.shopItemId && item.pId"
                      class="readonlyInput"
                      @click="copyValue(item.pId, $event)">
                      {{ item.pId }}
                    </p>
                    <input v-else
                      type="text"
                      v-model="item.pId"
                      :disabled="isReadonly || item.shopItemId"
                      class="input sizeSm">
                  </td>
                  <td v-if="type === 'card'">
                    <div v-if="item.thumb" class="itemThumb">
                      <img :src="item.thumb" alt="" class="thumbImg" />
                      <button type="button" class="delThumb" @click="deleteThumb(index)"><img src="~@/assets/img/ico_close.svg" alt=""></button>
                    </div>
                    <div v-else class="itemThumb">
                      <label class="btnUpload"><input type="file" @change="uploadImage($event, index)"></label></div>
                  </td>
                  <td>
                    <input type="text" class="input sizeS"
                      :disabled="isReadonly || type === 'select' || (isBaseShopPos && mustPosSync)"
                      @keyup="setItemQuantityMax(index)"
                      v-model="item.quantity.max" />
                  </td>
                  <td v-if="!(isBaseShopPos && mustPosSync)">
                    <button type="button" class="btn btnAdd" @click="setSubOption = item.optionItemId">+</button>
                  </td>
                  <td v-if="!isApply || !isBaseShopPos" class="center">
                    <button type="button" class="btn btnRed sizeXs" :disabled="isReadonly" @click="delOptionItem(index)">{{locale.delete}}</button>
                  </td>
                </tr>
              </draggable>
              <tbody v-else>
                <tr>
                  <td colspan="9" class="noData">
                    <span v-if="type === 'input'">{{locale.message_option_direct_input}}</span>
                    <span v-else>{{ locale.shop_message_add_option_items }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="btns">
        <button v-if="!isBaseShopPos && !isApply && (id && id !== 'new')" type="button" class="btn btnRed btnLeft btnAuto" @click="delItem">{{ locale.delete }}</button>
        <button type="button" class="btn btnWhite" @click="close">{{ locale.cancel }}</button>
        <button type="button" class="btn btnPrimary" :disabled="!isValid.result" @click="save">{{ locale.save }}</button>
      </div>
    </div>
    <ItemListAll
      v-if="itemListAll"
      :locale="locale"
      :isApply="isApply"
      @apply="applyOptionItem"
      @close="setItemListAll(false)" />
    <div
      v-show="setSubOption"
      class="subOptionList">
      <div class="subOptionTitle">
        {{ locale.select_suboption }}
        <button type="button" class="btnClose" @click="setSubOption = null"></button>
      </div>
      <div>
        <input type="text" class="input searchInput" v-model="searchWord" placeholder="option(preset) name or pid">
      </div>
      <OptionListAll
        ref="optionListAll"
        :locale="locale"
        :shopItemId="shopItemId"
        :filterIds="id && [id]"
        :searchWord="searchWord"
        @clicked="addSubOption" />
    </div>
    <!-- advancedSetting -->
    <div v-if="isAdvancedSetting" class="layer advancedLayer">
      <div class="inner">
        <h3 class="layerTitle">{{ locale.option_item_advanced_setting }}</h3>
        <div class="mb30">
          <div class="innerTitle setLabel">{{ locale.name }}</div>
          <SetLanguageValue
            :locale="locale"
            :langValue="advancedSetting.name"
            :disabled="isReadonly" />
        </div>
        <div class="mb30">
          <div class="innerTitle setLabel">{{ locale.option_item_name_on_list }}</div>
          <div class="checkbox mb10">
            <input type="checkbox" id="replace_name_on_list" :disabled="isReadonly" v-model="advancedSetting.replaceNameOnList">
            <label for="replace_name_on_list">{{ locale.activate }}</label>
          </div>
          <SetLanguageValue
            v-if="advancedSetting.replaceNameOnList"
            :locale="locale"
            :langValue="advancedSetting.replaceName"
            :disabled="isReadonly" />
        </div>
        <div class="btns">
          <button v-if="!isReadonly" type="button" class="btn btnWhite" @click="closeAdvancedSetting">{{ locale.cancel }}</button>
          <button v-if="!isReadonly" type="button" class="btn btnPrimary" @click="applyAdvancedOptionItem">{{ locale.apply }}</button>
          <button v-if="isReadonly" type="button" class="btn btnPrimary" @click="closeAdvancedSetting">{{ locale.confirm }}</button>
        </div>
      </div>
    </div>
    <!-- //advancedSetting -->
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import LANG from '@/common/lang'
import UTIL from '@/common/util'
import UNIQUE from '@/common/unique'
import regExp from '@/common/regExp'
import SetLanguageValue from '@/components/Util/SetLanguageValue'
import ItemListAll from './ItemListAll'
import OptionListAll from './OptionListAll'

export default {
  name: 'CreateOption',
  props: [
    'locale',
    'id',
    // 'mustPosSync',
    'shopItemId',
    'optionId',
    'isApply', //샵아이템에서 직접 옵션을 만들 경우
    'optionData',
    'savedOptionIds' // 저장된 스토어옵션id (아직 저장 안된 경우 스토어옵션정보 호출)
  ],
  components: {
    draggable,
    SetLanguageValue,
    ItemListAll,
    OptionListAll
  },
  computed: {
    defaultLang () {
      return this.$store?.state?.setting?.lang?.default || 'ko'
    },
    pageId () {
      return this.$route.params.id
    },
    isReadonly () {
      // return this.id && this.id !== 'new' && this.isApply //샵아이템에서 옵션구성을 할 때 스토어에 있는 옵션인 경우
      return false
    },
    getShopResultData () {
      return this.$store.getters['shop/getShopResultData']
    },
    activeItemIds () {
      return this.optionItems.filter(x => x.isActive).map(x => x.optionItemId)
    },
    isValid () {
      const defaultLang = this.defaultLang
      const isInputType = this.type === 'input'

      const isName = this.isEmptyStr(this.name[defaultLang])

      if (isInputType) {
        return {
          isValidMin: true,
          isValidMax: true,
          isValidMinMax: true,
          result: !isName
        }
      }

      const isCheckType = this.type === 'check'
      const min = this.min && Number(this.min)
      const max = this.max && Number(this.max)

      const isValidMinMax = isCheckType ? min <= max : true
      const hasItems = this.optionItems.length > 0

      const isItemName = this.optionItems.find(item => {
        return this.isEmptyStr(item.name[defaultLang])
      })

      const isItemActive = this.optionItems.some(item => item.isActive)
      const isLabels = !isName && !isItemName

      return {
        isValidMin: min > 0,
        isValidMax: max > 0,
        isValidMinMax,
        result: isLabels && isItemActive && isValidMinMax && hasItems
      }
    },
    isSavedOption () {
      return (this.id || this.optionId) && this.savedOptionIds && this.savedOptionIds.includes(this.id || this.optionId)
    },
  },
  watch: {
    type (type) {
      if (type === 'select') {
        this.min = 1
        this.max = 1
        this.optionItems = this.optionItems.map(item => {
          return {
            ...item,
            isDefault: false,
            quantity: {
              ...item.quantity,
              max: 1
            }
          }
        })
      }
    }
  },
  created () {
    this.init()
  },
  mounted () {
  },
  beforeRouteLeave (to, from, next) {
  },
  data () {
    return {
      isReady: false,
      title: '',
      name: LANG.valueObj(''),
      isRequired: true,
      isBaseShopPos: false, // POS 연동으로 생성된 옵션 여부
      mustPosSync: false, // 반드시 POS와 싱크가 맞아야 함 (제약사항이 더 많음)
      type: 'check', // check, select, card, input
      min: 1,
      max: 1,
      optionItems: [],
      langTarget: null,
      langValueLayer: false,
      langValue: {},
      itemListAll: false,
      setSubOption: null,
      searchWord: '',
      isAdvancedSetting: false,
      advancedSetting: null
    }
  },
  methods: {
    async init () {
      await this.getShopStoreOptionDetail()
    },
    isEmptyStr (str) {
      return regExp.replaceAllEmpty(str).length === 0
    },
    copyValue (val, e) {
      if (!val) return
      const target = e.currentTarget
      navigator.clipboard.writeText(val).then(() => {
        // 복사완료 토스트메시지
        const toast = document.createElement('span')
        toast.className = 'toastCopy'
        toast.textContent = 'Copy'
        target.appendChild(toast)
        setTimeout(() => {
          toast.style.opacity = '0'
          setTimeout(() => toast.remove(), 300)
        }, 300)
      })
    },
    addSubOption (option) {
      const optionItemId = this.setSubOption
      const lang = this.defaultLang
      const addOption = option && {id: option.id, name: option.title || option.name[lang]}
      this.optionItems = this.optionItems.map(item => {
        const isTarget = item.optionItemId === optionItemId
        if (!isTarget) return item
        // 기존 subOption + 새로 추가된 addOption
        const merged = [...(item.subOption || []), addOption]
        // 중복 제거 (id 기준)
        const deduped = Array.from(
          new Map(merged.map(opt => [opt.id, opt])).values()
        )
        return {
          ...item,
          isDefault: false,
          subOption: deduped
        }
      })
      this.setSubOption = null
    },
    delSubOption (optionItemId, subOptionId) {
      this.optionItems = this.optionItems.map(item => {
        const isTarget = item.optionItemId === optionItemId
        if (!isTarget) return item
        const filtered = (item.subOption || []).filter(opt => opt.id !== subOptionId)
        if (filtered.length === 0) {
          const { subOption, ...rest } = item
          return { ...rest }
        }
        return {
          ...item,
          subOption: filtered
        }
      })
    },
    uploadImage (e, index) {
      const data = {
        id: UNIQUE.mid(),
        file: e.target.files[0],
        addThumb: true
      }
      const isImage = regExp.image.test(data.file.name.slice(data.file.name.lastIndexOf('.') + 1).toLowerCase())
      if (isImage) {
        this.$store.dispatch('util/uploadFile', data).then((res) => {
          this.optionItems = this.optionItems.map((x, i) => {
            return {
              ...x,
              ...index === i && {
                thumb: res.source
              }
            }
          })
          e.target.value = ''
        })
      } else {
        alert(this.locale.validate_upload_image)
      }
    },
    deleteThumb (index) {
      this.optionItems = this.optionItems.map((x, i) => {
        return {
          ...x,
          ...index === i && {
            thumb: ''
          }
        }
      })
    },
    itemShopExcludeTax (pageId) {
      const pageProperty = this.$store.getters['shop/getShopProperty'](pageId)
      return pageProperty?.excludeTax
    },
    async getShopStoreOptionDetail () {
      const isItemOption = this.shopItemId
      if (this.id && this.id !== 'new') {
        this.$store.commit('onLoading', true)
        const getDetailInfo = isItemOption && this.isSavedOption
          ? await this.$store.dispatch('shop/getShopItemOption', { optionId: this.id, shopItemId: this.shopItemId })
          : await this.$store.dispatch('shop/getShopStoreOption', this.id)
        const detailInfo = getDetailInfo && getDetailInfo.message
        // 스토어 옵션이다?
        if (detailInfo && detailInfo.id) {
          this.title = detailInfo.title || detailInfo.name[this.defaultLang]
          this.name = detailInfo.name
          this.isRequired = detailInfo.isRequired
          this.isBaseShopPos = detailInfo.isBaseShopPos || false
          this.mustPosSync = detailInfo.mustPosSync || false
          this.type = detailInfo.type
          this.optionItems = detailInfo.optionItems.map(x => {
            return {
              ...x,
              price: this.itemShopExcludeTax(this.pageId) ? (x.priceInfo?.net || x.priceInfo?.gross) : x.priceInfo?.gross || x.price,
              quantity: {
                max: (x.quantity && x.quantity.max) || 1
              },
              showPlus: x.showPlus === true
            }
          })
          if (detailInfo.typeOption) {
            this.min = detailInfo.typeOption.min
            this.max = detailInfo.typeOption.max
          }
        }
        this.$store.commit('onLoading', false)
      // 상품상세에서 직접만든 옵션이다?
      } else if (this.optionData && this.optionData !== 'new') {
        const detailInfo = this.optionData
        if (detailInfo) {
          this.name = detailInfo.name
          this.isRequired = detailInfo.isRequired
          this.type = detailInfo.type
          this.optionItems = detailInfo.optionItems
          if (detailInfo.typeOption) {
            this.min = detailInfo.typeOption.min
            this.max = detailInfo.typeOption.max
          }
        }
      }
      this.isReady = true
    },
    setValue (target) {
      const val = this[target]
      const value = regExp.priceInput(val)
      this[target] = value || ''
    },
    setOptionPrice (item) {
      const val = item.price
      const value = regExp.priceInput(val)
      item.price = value || ''
    },
    isValidSubOption (optId) {
      const optionListAll = this.$refs?.optionListAll?.list
      const isValid = optionListAll?.filter(opt => opt.id === optId)?.length > 0
      return isValid
    },
    getSubOptionName (optId) {
      const defaultLang = this.defaultLang
      const optionListAll = this.$refs?.optionListAll?.list
      const subOption = optionListAll?.find(opt => opt.id === optId)
      return (subOption?.title || subOption?.name?.[defaultLang]) || '-'
    },
    addOptionItem () {
      this.optionItems.push({
        optionItemId: UNIQUE.uniqueId8(),
        name: LANG.valueObj(''),
        isDefault: false,
        pId: '',
        price: '',
        isActive: true,
        quantity: {
          max: 1
        },
        subOption: [],
        replaceNameOnList: false,
        replaceName: LANG.valueObj(''),
        showPlus: false
      })
    },
    activateItem (optionIndex) {
      this.optionItems = this.optionItems.map((x, index) => {
        const activateItem = x.isActive
        return {
          ...x,
          isActive: index === optionIndex ? !activateItem : x.isActive
        }
      })
    },
    delOptionItem (optionIndex) {
      this.optionItems = this.optionItems.filter((x, index) => index !== optionIndex)
    },
    applyOptionItem (item) { // 상품아이템을 옵션으로 추가
      this.optionItems.push({
        isActive: true,
        shopItemId: item.shopItemId,
        optionItemId: UNIQUE.uniqueId8(),
        name: item.shopItemName,
        isDefault: false,
        pId: item.pId || '',
        price: this.itemShopExcludeTax(item.pageId) ? (item.priceInfo?.net || item.priceInfo?.gross) : item.priceInfo?.gross || x.price,
        quantity: {
          max: 1
        },
        replaceNameOnList: false,
        replaceName: LANG.valueObj(''),
      })
    },
    setItemListAll (isOpen) {
      this.itemListAll = isOpen
    },
    setItemQuantityMax (optionIndex) {
      const val = this.optionItems[optionIndex].quantity.max
      const value = regExp.naturalNumberOnly(val)
      this.optionItems[optionIndex].quantity.max = parseInt(value) || ''
    },
    setDefaultItem (event, optionIndex) {
      const min = this.min
      const max = this.max
      const selectedCount = this.optionItems.filter(item => item.isDefault).length
      const targetItem = this.optionItems[optionIndex]
      const isChecked = event?.target?.checked || false
      if (min === 1 && max === 1) {
        // 기존 선택 해제 후 새로운 선택만 유지 (radio와 동일한 동작)
        this.optionItems = this.optionItems.map((x, index) => {
          return {
            ...x,
            isDefault: isChecked ? index === optionIndex : false
          }
        })
      } else {
        // 선택 시 최대 개수(max) 초과 방지
        if (!targetItem.isDefault && selectedCount >= max) {
          event.preventDefault()
          return
        }
        // 값 토글
        targetItem.isDefault = !targetItem.isDefault
      }
    },
    closeAdvancedSetting () {
      this.isAdvancedSetting = false
    },
    setAdvancedSetting (item) {
      const defaultAdvancedSetting = {
        replaceNameOnList: false,
        replaceName: LANG.valueObj(''),
      }
      const optionItem = {
        ...defaultAdvancedSetting,
        ...item
      }
      this.isAdvancedSetting = true
      this.advancedSetting = optionItem
    },
    applyAdvancedOptionItem () {
      const advancedItemId = this.advancedSetting.optionItemId
      const optionItem = this.optionItems?.find(item => item.optionItemId === advancedItemId)
      if (optionItem && window.confirm(this.locale.confirm_apply)) {
        this.optionItems = this.optionItems.map(item => {
          return {
            ...item,
            ...item.optionItemId === advancedItemId && {
              ...this.advancedSetting
            }
          }
        })
      }
      this.closeAdvancedSetting()
    },
    close () {
      this.$emit('close')
    },
    async delItem () {
      if (window.confirm(this.locale.confirm_delete_option)) {
        this.$store.commit('onLoading', true)
        const res = await this.$store.dispatch('shop/delStoreOption', this.id)
        if (res && res.status === 200) {
          alert(this.locale.complete_delete)
          this.$emit('reload')
          this.close()
        } else {
          const status = (res && res.message && res.message.code) || 500
          const message = this.getShopResultData(status)
          alert(message)
        }
        this.$store.commit('onLoading', false)
      }
    },
    async save () {
      const data = {
        id: (!this.id || this.id === 'new') ? UNIQUE.uniqueId8() : this.id,
        title: this.title,
        name: this.name,
        type: this.type,
        isRequired: this.isRequired,
        optionItems: this.optionItems,
        ...(this.type === 'check' || this.type === 'card') && {
          typeOption: {
            min: this.min,
            max: this.max
          }
        },
        activeItemIds: this.activeItemIds
      }
      this.$store.commit('onLoading', true)
      const res = await this.$store.dispatch('shop/putShopStoreOption', data)
      if (res.status === 200) {
        alert(this.locale.complete_save)
        if (this.isApply) {
          this.$emit('apply', data)
        }
        this.$emit('reload')
        this.close()
      } else {
        const status = (res && res.message && res.message.code) || 500
        const message = this.getShopResultData(status)
        alert(message)
      }
      this.$store.commit('onLoading', false)
    }
  }
}
</script>

<style lang="scss" scoped>
.layer {
  .inner {
    max-width: 1280px;
  }
}
.optId {
  font-size: 12px;
  font-weight: normal;
  opacity: 0.5;
}
.innerContent {
  display: flex;
  flex: 1;
  overflow: hidden;
  .info {
    flex: 1;
    overflow: auto;
    padding: 30px;
    border: 1px solid $color-lgrey2;
    border-radius: 4px;
    background-color: $color-white;
    & + .info {
      margin-left: 30px;
    }
  }
  .setOptionItems {
    flex: 3.5;
  }
}
.subOptionList {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  max-height: 100%;
  margin-left: 8px;
  margin-right: -8px;
  padding: 12px;
  border-radius: 8px;
  background-color: $bg-default;
  .btnClose {
    width: 30px;
    height: 30px;
    background: url('~@/assets/img/ico_close_black.svg') 50% 50% / 12px no-repeat;
    opacity: 0.4;
  }
  .subOptionTitle {
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: bold;
  }
  .shopOptionList {
    flex: 1;
    overflow: auto;
  }
}
.isRequired {
  .innerTitle {
    padding-left: 18px;
    &:after {
      content: "*";
      position: absolute;
      top: 0;
      left: 8px;
      color: $color-red;
    }
  }
}
.setOptionItems {
  .btn {
    & + .btn {
      margin-left: 4px;
    }
  }
}
.itemThumb {
  position: relative;
  height: 44px;
  border: 1px solid $color-lgrey2;
  border-radius: 4px;
  background-color: $color-white;
  .thumbImg {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
  .delThumb {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 22px;
    height: 22px;
    border-radius: 100%;
    background-color: rgba($color-dark, 0.5);
    img {
      width: 10px;
    }
  }
  .btnUpload {
    display: block;
    width: 100%;
    height: 100%;
    background: $color-lgrey2 url('~@/assets/img/ico_upload_image.png') 50% 50% / 50% no-repeat;
    opacity: 0.2;
  }
}
.subOptionItems {
  padding-left: 16px;
  overflow: hidden;
  .subOption {
    position: relative;
    margin-top: 4px;
    padding: 4px 24px 4px 8px;
    font-size: 12px;
    color: $color-grey;
    border: 1px solid $color-lgrey2;
    border-radius: 4px;
    background-color: $bg-default;
    &:before,
    &:after {
      content: "";
      position: absolute;
      right: 100%;
      bottom: 50%;
    }
    &:before {
      width: 12px;
      border-bottom: 1px solid $color-lgrey2;
    }
    &:after {
      height: calc(100% + 6px);
      margin-right: 12px;
      border-right: 1px solid $color-lgrey2;
    }
    .btnDel {
      position: absolute;
      top: 0;
      right: 0;
      width: 24px;
      height: 100%;
      background: url('~@/assets/img/ico_close_black.svg') 50% 50% / 12px no-repeat;
      opacity: 0.5;
    }
    &.isDeleted {
      background-color: rgba($color-red, 0.1);
      span {
        color: $color-red;
        font-size: 10px;
      }
    }
  }
}
.itemCheck {
  .checkbox {
    margin-right: 0;
  }
}
.optionItemName {
  position: relative;
}
.optionItemPrice {
  position: relative;
  padding-left: 16px;
  .additionalCharge {
    position: absolute;
    top: 16px;
    left: 0;
    width: 24px;
    height: 24px;
    font-size: 16px;
    line-height: 1;
    color: $color-lgrey;
    border: 1px dashed $color-lgrey2;
    border-radius: 100%;
    background-color: #fff;
    &.isActive {
      color: $color-dark;
      border: 1px solid $color-grey;
    }
  }
}
.theadTip {
  font-size: 10px;
  font-weight: normal;
  color: $color-grey;
  margin-bottom: -1em;
}
.btnSetting {
  position: absolute;
  top: 2px;
  left: -4px;
  width: 18px;
  height: 18px;
  border-radius: 100%;
  background-color: $color-white;
  &:before {
    content: "";
    display: block;
    width: 100%;
    height: 100%;
    background: url('~@/assets/img/ico_setting.svg') 50% 50% / 16px no-repeat;
    opacity: 0.4;
  }
}
.advancedLayer {
  .inner {
    max-width: 400px;
  }
}
</style>
